<template>
  <div v-if="openInput" class="input-group flex-nowrap">
    <button v-if="showChangeControls" class="btn btn-primary" type="button" @click="decreaseQuantity" :disabled="quantity <= minQuantity">
      <font-awesome-icon :icon="['fas', 'minus']" />
    </button>
    <input type="number" class="form-control highlight-fade" :class="{ highlight: showDiffAnim && highlightQuantity }" :min="minQuantity" max="1000" size="4" style="min-width: 32px; max-width: 100px;" :value="quantity" @input="updateQuantity" @focus="($event.target as HTMLInputElement).select()" :data-field-id="fieldId">
    <span v-if="showDiffAnim">
      <span v-for="(popup, index) in popups" :key="index" :class="['popup', popup.colorClass, popup.directionClass]">{{ popup.text }}</span>
    </span>
    <span v-if="showUnit" class="input-group-text text-secondary">{{ unitName }}</span>
    <button v-if="showChangeControls" class="btn btn-primary" type="button" @click="increaseQuantity" :disabled="quantity >= 1000">
      <font-awesome-icon :icon="['fas', 'plus']" />
    </button>
  </div>
  <div v-else class="input-group flex-nowrap">
    <div class="ms-1 me-2" v-if="showChangeControls">
      <button class="btn btn-primary" type="button" @click="decreaseQuantity" :disabled="quantity <= minQuantity">
        <font-awesome-icon :icon="['fas', 'minus']" />
      </button>
    </div>
    <span class="highlight-fade" :class="{ highlight: showDiffAnim && highlightQuantity }">
      <span v-if="estimatedQuantity !== undefined && estimatedQuantity !== quantity">
        <span v-if="padNumbers" class="text-transparent">{{ pad(estimatedQuantity) }}</span>{{ estimatedQuantity }}
        <sub class="text-secondary"> <span v-if="padNumbers" class="text-transparent">{{ pad(quantity) }}</span>
          <span>{{ quantity }}</span>
          <span v-if="showDiffAnim">
            <span v-for="(popup, index) in popups" :key="index" :class="['popup', popup.colorClass, popup.directionClass]">{{ popup.text }}</span>
          </span>
        </sub>
      </span>
      <span v-else>
        <span v-if="padNumbers" class="text-transparent fs-5">{{ pad(quantity) }}</span>
        <span class="fs-5">{{ quantity }}</span>
        <span v-if="showDiffAnim">
          <span v-for="(popup, index) in popups" :key="index" :class="['popup', popup.colorClass, popup.directionClass]">{{ popup.text }}</span>
        </span>
      </span>
      <div v-if="showUnit" class="d-inline">
        <sup v-if="!inlineUnitName && lessThanLg && !showChangeControls" class="text-nowrap"><br>{{ unitName }}</sup>
        <sup v-else class="ms-1 text-nowrap">{{ unitName }}</sup>
      </div>
    </span>
    <div class="ms-1 me-2" v-if="showChangeControls">
      <button class="btn btn-primary" type="button" @click="increaseQuantity" :disabled="quantity >= 1000">
        <font-awesome-icon :icon="['fas', 'plus']" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  quantity: { type: Number, required: true },
  estimatedQuantity: { type: Number, required: false },
  unitName: { type: String, required: true },
  showUnit: { type: Boolean, required: false, default: true },
  padLength: { type: Number, required: false, default: 5, min: 0, max: 5 },
  showDiffAnim: { type: Boolean, required: false, default: false },
  showChangeControls: { type: Boolean, required: false, default: false },
  openInput: { type: Boolean, required: false, default: false },
  minQuantity: { type: Number, required: false, default: 1 },
  fieldId: { type: String, required: false },
  inlineUnitName: { type: Boolean, required: false, default: false }
});

const emit = defineEmits(['update:quantity']);

const padNumbers = ref(props.padLength > 0);

const highlightQuantity = ref(false);
interface Popup {
  id: number;
  text: string;
  colorClass: string;
  directionClass: string;
}

const popups = ref<Popup[]>([]);
let popupId = 0;

// Track if window width is less than 992px
const lessThanLg = ref(window.innerWidth < 992);

const handleResize = () => {
  lessThanLg.value = window.innerWidth < 992;
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  handleResize();
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

watch(() => props.quantity, (newQuantity, oldQuantity) => {
  const difference = newQuantity - oldQuantity;
  const popupText = (difference >= 0 ? '+' : '') + difference; // the minus sign is already included in the difference value
  const popupColorClass = difference > 0 ? 'popup-positive' : 'popup-negative';
  const popupDirectionClass = difference > 0 ? 'slide-up' : 'slide-down';

  popups.value.push({
    id: popupId++,
    text: popupText,
    colorClass: popupColorClass,
    directionClass: popupDirectionClass
  });

  highlightQuantity.value = true;
  setTimeout(() => {
    highlightQuantity.value = false;
  }, 1000);

  setTimeout(() => {
    const index = popups.value.findIndex(p => p.id === popupId);
    if (index !== -1) {
      popups.value.splice(index, 1);
    }
  }, 2000);
});

const pad = (n: number) => "0".repeat(Math.max(props.padLength - n.toString().length, 0));

const updateQuantity = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newQuantity = parseInt(target.value);
  if (newQuantity >= 0 && newQuantity <= 1000) {
    emit('update:quantity', newQuantity);
  }
};
const increaseQuantity = () => {
  emit('update:quantity', props.quantity + 1);
};
const decreaseQuantity = () => {
  emit('update:quantity', props.quantity - 1);
};
</script>

<style scoped>
.highlight-fade {
  transition: background-color 3s ease-in;
}

.highlight-fade.highlight {
  transition: background-color 0.5s ease-out;
}

.highlight {
  background-color: var(--bs-primary-bg-subtle);
}

.popup {
  position: absolute;
  font-size: 2em;
  animation-duration: 2s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
}

.popup-positive {
  color: var(--bs-success);
}

.popup-negative {
  color: var(--bs-danger);
}

.slide-up {
  animation-name: slide-up;
}

.slide-down {
  animation-name: slide-down;
}

@keyframes slide-up {
  0% {
    opacity: 1;
    transform: translate(-32px, -16px);
  }
  100% {
    opacity: 0;
    transform: translate(-32px, -48px);
  }
}

@keyframes slide-down {
  0% {
    opacity: 1;
    transform: translate(-24px, -8px);
  }
  100% {
    opacity: 0;
    transform: translate(-24px, 24px);
  }
}
</style>